import { ArrowDropDown as ArrowDropDownIcon, Cancel as CancelIcon, ClearOutlined } from '@mui/icons-material';
import { Chip, IconButton } from '@mui/material';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { useVNFieldHelpContext, VNFieldHelpIconButton } from '@/components/VNFieldHelp';

import VNDropdownList from './options/VNDropdownList';
import type { DisplaySlotProps, DropdownOption, ListSlotProps } from './types';
import { filterOptionsWithAncestors } from './types';

// ─── Shared query context ─────────────────────────────────────────────────────

export interface AutocompleteCtx {
  query: string;
  setQuery: (q: string) => void;
  // Written by AutocompleteList; read by AutocompleteDisplay on Enter.
  selectFocusedRef: React.MutableRefObject<(() => void) | null>;
  listComponent?: React.ComponentType<ListSlotProps>;
}

const _fallbackRef: React.MutableRefObject<(() => void) | null> = { current: null };

export const AutocompleteContext = createContext<AutocompleteCtx>({
  query: '',
  setQuery: () => {},
  selectFocusedRef: _fallbackRef,
});

// ─── Display slot: inline search overlay ─────────────────────────────────────

export const AutocompleteDisplay = ({
  selectedOptions,
  isOpen,
  onToggle,
  onClearOption,
  label,
  placeholder = 'Select...',
  disabled = false,
  error,
  multiple = false,
  inputRef,
  triggerId,
  listboxId,
  focusedOptionId,
  onKeyDown,
  disableBorder = false,
  allowClear = false,
  onClearAll,
  hideFloatingLabel = false,
}: DisplaySlotProps) => {
  const { iconConfig: fieldHelpIconConfig } = useVNFieldHelpContext();
  const { query, setQuery, selectFocusedRef } = useContext(AutocompleteContext);
  const searchRef = useRef<HTMLInputElement>(null);

  const handleInputKeyDown = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        selectFocusedRef.current?.();
        return;
      }
      if (e.key === ' ') {
        return;
      }
      (onKeyDown as unknown as React.KeyboardEventHandler<HTMLInputElement>)(e);
    },
    [selectFocusedRef, onKeyDown]
  );

  const hasValue = selectedOptions.length > 0;
  const isLabelFloating = isOpen || hasValue || query.length > 0;
  const errorId = `${triggerId}-error`;
  const showClearButton = allowClear && hasValue && !disabled && !isOpen;

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      return undefined;
    }
    const id = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [isOpen, setQuery]);

  return (
    <div
      className={[
        'vn-dropdown-trigger',
        error ? 'vn-dropdown-trigger--error' : '',
        disabled ? 'vn-dropdown-trigger--disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {label && !hideFloatingLabel && (
        <label
          htmlFor={isOpen ? undefined : triggerId}
          className={['vn-dropdown-trigger__label', isLabelFloating ? 'vn-dropdown-trigger__label--floating' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {label}
        </label>
      )}

      {!isOpen && (
        <button
          id={triggerId}
          ref={inputRef as React.Ref<HTMLButtonElement>}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={false}
          aria-controls={listboxId}
          aria-invalid={!!error || undefined}
          aria-describedby={error ? errorId : undefined}
          disabled={disabled}
          className={[
            'vn-dropdown-display',
            disabled ? 'vn-dropdown-display--disabled' : '',
            error ? 'vn-dropdown-display--error' : '',
            disableBorder ? 'vn-dropdown-display--borderless' : '',
            showClearButton ? 'vn-dropdown-display--with-clear' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={onToggle}
          onKeyDown={onKeyDown}
        >
          <span className="vn-dropdown-display__content">
            {!hasValue ? (
              <span className="vn-dropdown-display__placeholder">{!label || isLabelFloating ? placeholder : ''}</span>
            ) : multiple ? (
              <div className="vn-dropdown-display__chips">
                <Chip
                  key={selectedOptions[0].id}
                  label={selectedOptions[0].label}
                  title={selectedOptions[0].label}
                  size="small"
                  onDelete={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onClearOption(selectedOptions[0].id);
                  }}
                  deleteIcon={<CancelIcon onMouseDown={(e: React.MouseEvent) => e.stopPropagation()} />}
                />
                {selectedOptions.length > 1 && (
                  <span className="vn-dropdown-display__overflow-badge">+{selectedOptions.length - 1}</span>
                )}
              </div>
            ) : (
              <span className="vn-dropdown-display__value">{selectedOptions[0].label}</span>
            )}
          </span>
          <span className="vn-dropdown-display__trailing">
            <ArrowDropDownIcon className="vn-dropdown-display__caret" fontSize="small" aria-hidden="true" />
            {fieldHelpIconConfig && (
              <span
                className="vn-dropdown-trigger__field-help"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <VNFieldHelpIconButton {...fieldHelpIconConfig} />
              </span>
            )}
          </span>
        </button>
      )}

      {showClearButton && (
        <IconButton
          size="small"
          className="vn-dropdown-display__clear-btn"
          onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onClearAll?.();
          }}
          aria-label="Clear selection"
          tabIndex={-1}
        >
          <ClearOutlined fontSize="small" />
        </IconButton>
      )}

      {isOpen && (
        <div
          className={[
            'vn-dropdown-display',
            'vn-dropdown-display--open',
            'vn-dropdown-autocomplete__inline',
            error ? 'vn-dropdown-display--error' : '',
            disableBorder ? 'vn-dropdown-display--borderless' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {multiple && selectedOptions.length > 0 && (
            <div className="vn-dropdown-autocomplete__chip-scroll" onMouseDown={(e) => e.stopPropagation()}>
              {selectedOptions.map((opt: DropdownOption) => (
                <Chip
                  key={opt.id}
                  label={opt.label}
                  title={opt.label}
                  size="small"
                  onDelete={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onClearOption(opt.id);
                  }}
                  deleteIcon={<CancelIcon />}
                />
              ))}
            </div>
          )}

          <div
            className="vn-dropdown-autocomplete__bottom-row"
            onClick={() => searchRef.current?.focus()}
            onMouseDown={(e) => e.preventDefault()}
          >
            <input
              ref={searchRef}
              type="text"
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded
              aria-controls={listboxId}
              aria-activedescendant={focusedOptionId}
              aria-autocomplete="list"
              aria-label={label ?? placeholder}
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onMouseDown={(e) => e.stopPropagation()}
              placeholder={!hasValue ? placeholder : ''}
              className="vn-dropdown-autocomplete__search-input"
            />

            <span className="vn-dropdown-display__trailing">
              <button
                type="button"
                tabIndex={-1}
                className="vn-dropdown-display__caret-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                aria-label="Close dropdown"
              >
                <ArrowDropDownIcon
                  className="vn-dropdown-display__caret vn-dropdown-display__caret--open"
                  fontSize="small"
                  aria-hidden="true"
                />
              </button>
              {fieldHelpIconConfig && (
                <span
                  className="vn-dropdown-trigger__field-help"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <VNFieldHelpIconButton {...fieldHelpIconConfig} />
                </span>
              )}
            </span>
          </div>
        </div>
      )}

      {error && (
        <p id={errorId} className="vn-dropdown-trigger__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

// ─── List slot: filter by shared query ───────────────────────────────────────

export const AutocompleteList = (props: ListSlotProps) => {
  const { query, setQuery, selectFocusedRef, listComponent } = useContext(AutocompleteContext);
  const ListComponent = listComponent ?? VNDropdownList;

  const filteredOptions = useMemo(() => filterOptionsWithAncestors(props.options, query), [props.options, query]);

  const clampedFocusedIndex = props.focusedIndex < filteredOptions.length ? props.focusedIndex : 0;

  const { onSelect } = props;
  const onSelectAndClear = useCallback(
    (option: DropdownOption) => {
      onSelect(option);
      setQuery('');
    },
    [onSelect, setQuery]
  );

  useLayoutEffect(() => {
    selectFocusedRef.current = filteredOptions.length > 0 ? () => onSelectAndClear(filteredOptions[clampedFocusedIndex]) : null;
  }, [filteredOptions, clampedFocusedIndex, onSelectAndClear, selectFocusedRef]);

  return <ListComponent {...props} options={filteredOptions} focusedIndex={clampedFocusedIndex} onSelect={onSelectAndClear} />;
};
